/**
 * Audio playback module for Gemini Live API output.
 *
 * Gemini returns audio as base64-encoded 16-bit PCM mono at 24 kHz.
 * Chunks arrive as many small bursts; we schedule them sequentially
 * on an AudioBufferSourceNode timeline to prevent gaps or overlaps.
 *
 * Turn-complete logic (two-signal approach):
 *   Signal 1: Server sends { serverContent: { turnComplete: true } }
 *             → set serverTurnDone = true
 *   Signal 2: Last AudioBufferSourceNode fires 'ended', after 300 ms debounce
 *             → set audioQueueEmpty = true
 *   When BOTH are true → fire onTurnComplete()
 *
 * This is critical because audio is still playing when the server signals done.
 */

const GEMINI_OUTPUT_SAMPLE_RATE = 24000;
const SPEAKING_STOP_DEBOUNCE_MS = 300;

export interface PlaybackCallbacks {
  /** AI started producing audio. Use for UI animation. */
  onAiSpeakingStart: () => void;
  /** AI finished producing audio (debounced). Use for UI animation. */
  onAiSpeakingStop: () => void;
  /**
   * Both server turnComplete AND audio queue are empty.
   * Use for game logic / session state transitions.
   */
  onTurnComplete: () => void;
  /** RMS of the current output chunk [0–1], for the visualizer. */
  onOutputRms: (rms: number) => void;
}

export class AudioPlayback {
  private audioContext: AudioContext | null = null;
  private nextStartTime = 0;
  private activeSources = new Set<AudioBufferSourceNode>();
  private serverTurnDone = false;
  private speakingDebounce: ReturnType<typeof setTimeout> | null = null;
  private callbacks: PlaybackCallbacks;

  constructor(callbacks: PlaybackCallbacks) {
    this.callbacks = callbacks;
  }

  private _getOrCreateContext(): AudioContext {
    if (!this.audioContext) {
      // Create lazily so it happens after a user gesture (autoplay policies).
      this.audioContext = new AudioContext({ sampleRate: GEMINI_OUTPUT_SAMPLE_RATE });
    }
    // If the context was created before a user gesture, it can be suspended.
    if (this.audioContext.state !== "running") {
      // Fire-and-forget; we don't want to block scheduling.
      void this.audioContext.resume().catch(() => {});
    }
    return this.audioContext;
  }

  /**
   * Enqueue a base64-encoded PCM chunk from Gemini.
   * Called for every audio delta message received from the server.
   */
  enqueueBase64Chunk(base64: string): void {
    const ctx = this._getOrCreateContext();
    const binaryStr = atob(base64);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }

    // Interpret as Int16 PCM
    const int16 = new Int16Array(bytes.buffer);
    const float32 = new Float32Array(int16.length);
    let sumSq = 0;
    for (let i = 0; i < int16.length; i++) {
      const s = int16[i] / 32768.0;
      float32[i] = s;
      sumSq += s * s;
    }
    const rms = Math.sqrt(sumSq / int16.length);
    this.callbacks.onOutputRms(rms);

    const buffer = ctx.createBuffer(
      1,
      float32.length,
      GEMINI_OUTPUT_SAMPLE_RATE
    );
    buffer.copyToChannel(float32, 0);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);

    // Schedule sequentially — nextStartTime prevents gaps between chunks
    const startAt = Math.max(ctx.currentTime, this.nextStartTime);
    source.start(startAt);
    this.nextStartTime = startAt + buffer.duration;

    this.activeSources.add(source);

    // Cancel any pending stop debounce — audio is clearly still playing
    if (this.speakingDebounce) {
      clearTimeout(this.speakingDebounce);
      this.speakingDebounce = null;
    }
    // Signal start immediately on first chunk
    if (this.activeSources.size === 1) {
      this.callbacks.onAiSpeakingStart();
    }

    source.addEventListener("ended", () => {
      this.activeSources.delete(source);

      if (this.activeSources.size === 0) {
        // Debounce stop to handle brief gaps between chunks
        this.speakingDebounce = setTimeout(() => {
          this.callbacks.onAiSpeakingStop();
          if (this.serverTurnDone) {
            this._fireTurnComplete();
          }
        }, SPEAKING_STOP_DEBOUNCE_MS);
      }
    });
  }

  /**
   * Called when the server sends { serverContent: { turnComplete: true } }.
   * If audio is still playing, we defer until playback finishes.
   */
  signalServerTurnComplete(): void {
    this.serverTurnDone = true;
    if (this.activeSources.size === 0) {
      // No audio in flight — fire immediately
      this._fireTurnComplete();
    }
    // Otherwise, the 'ended' handler will call _fireTurnComplete after debounce
  }

  /** Reset turn state at the start of each new model turn. */
  resetTurnState(): void {
    this.serverTurnDone = false;
    if (this.speakingDebounce) {
      clearTimeout(this.speakingDebounce);
      this.speakingDebounce = null;
    }
  }

  /** Stop all currently playing audio (e.g. on barge-in / interruption). */
  interrupt(): void {
    this.activeSources.forEach((src) => {
      try { src.stop(); } catch { /* already stopped */ }
    });
    this.activeSources.clear();
    this.nextStartTime = 0;
    if (this.speakingDebounce) {
      clearTimeout(this.speakingDebounce);
      this.speakingDebounce = null;
    }
    this.callbacks.onAiSpeakingStop();
  }

  destroy(): void {
    this.interrupt();
    if (this.audioContext) {
      void this.audioContext.close();
      this.audioContext = null;
    }
  }

  private _fireTurnComplete(): void {
    this.serverTurnDone = false;
    this.callbacks.onTurnComplete();
  }
}
